import type { IssueFieldChange } from '@cortex/domain-ac-mens-hub-api';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 課題の項目を保存し、結果をトーストで知らせる関数。保存できたら true。
 * @graph-connects none
 */
export type SaveIssueFields = (changes: IssueFieldChange[], message: string) => Promise<boolean>;

/**
 * 課題の項目（タイトル・中身・テーマ・領域など）を保存する。保存できたら message を「元に戻す」つきで知らせ、
 * 伏せ字を当てたらそれも添える。他の人が先に変えていたら、誰が何に変えたかを出す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の項目を開いた時点の値つきで保存し、保存できたら元に戻せる知らせを、競合したら誰が何に変えたかを出す
 * @graph-connects useIssueMgmtWrite [calls] updateIssue
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function useIssueFieldSave(issueId: string): SaveIssueFields {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const update = useIssueMgmtWrite('updateIssue');
  const { mutateAsync } = update;
  return useCallback(
    async (changes, message) => {
      try {
        const result = await mutateAsync({ issueId, changes });
        toast.notifySaved(result.operationId === null ? t('issueMgmt.common.unchanged') : message, {
          operationId: result.operationId,
          ...(result.maskedFields.length > 0 ? { detail: t('issueMgmt.form.masked') } : {}),
        });
        return true;
      } catch (error) {
        toast.notifyError(error);
        return false;
      }
    },
    [issueId, mutateAsync, t, toast],
  );
}
